import { functionalUpdate } from '@tanstack/react-table';
import type { ScrollToOptions } from '@tanstack/react-virtual';
import {
  useCallback,
  useEffect,
  useRef,
  type PointerEvent,
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { useIntl } from 'react-intl';

import type {
  DataTableRowActivationEvent,
  DataTableRowInteractivityProps,
} from './row-interactivity-types.js';
import * as styles from './RowInteractivity.sty.js';
import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import { useFocusRing } from '../../../../core/styles/useFocusRing.js';
import { mergeProps as _mergeProps } from '../../../../core/utils/merge-props.js';
import { isTargetInteractive } from '../../../utils/is-target-interactive.js';
import { DATA_TABLE_ROW_EXPANDED_ID_SUFFIX } from '../../constants.js';
import { useDataTable } from '../../contexts/DataTableContext.js';
import type {
  Row,
  Table,
  TableFeature,
  TableOptions,
  Updater,
} from '../../hooks/useTable/types.js';
import { assignRowMethods } from '../../hooks/useTable/utils.js';
import { dataTableMessages } from '../../messages.js';
import type { DataTableRowData } from '../../public.api.js';

function removeHoverStyles(virtualizationContainer?: Element): void {
  if (virtualizationContainer) {
    const hoveredRows = virtualizationContainer.querySelectorAll(
      `.${styles.interactiveRow.classNames.variants.hovered.true}`,
    );
    for (const hoveredRow of Array.from(hoveredRows)) {
      hoveredRow.classList.remove(
        styles.interactiveRow.classNames.variants.hovered.true,
      );
    }
  }
}

function addHoverStyles(row: HTMLElement): void {
  row.classList.add(styles.interactiveRow.classNames.variants.hovered.true);
}

/**
 * Applies hover styles to a row by its ID within the virtualization container.
 */
function applyHoverStylesByRowId(
  virtualizationContainer: Element | undefined,
  rowId: string,
): void {
  if (!virtualizationContainer) {
    return;
  }
  const rowElement = virtualizationContainer.querySelector<HTMLElement>(
    `[data-rowid="${CSS.escape(rowId)}"]`,
  );
  if (rowElement) {
    addHoverStyles(rowElement);
  }
}

/**
 * When a cell/row actions menu is open, we need to ensure hover styles remain on the row
 * that the menu belongs to.
 * @param table - the table instance
 * @param virtualizationContainer - the virtualization container element
 * @returns `true` if hover styles were maintained, meaning that any other hover logic can be skipped
 */
function maintainOpenMenuRowHover<TData>(
  table: Table<TData>,
  virtualizationContainer: Element | undefined,
): boolean {
  const openMenuRowId = table.getOpenMenuRowId();
  if (openMenuRowId === null) {
    return false;
  }
  if (table.getHoveredRow() !== openMenuRowId) {
    table.setHoveredRow(openMenuRowId);
    removeHoverStyles(virtualizationContainer);
    applyHoverStylesByRowId(virtualizationContainer, openMenuRowId);
  }
  return true;
}

/**
 * Provides all necessary props for an interactive row element.
 *
 * @param row - the table row element
 * @param scrollToIndex - rowVirtualizer function for scrolling to the row with the specified index
 * @returns className and props for the row element
 */
export function useRowInteractivityProps<TData extends DataTableRowData>(
  row: Row<TData>,
  scrollToIndex: (index: number, options?: ScrollToOptions) => void,
): {
  rowInteractivityClassName: string;
  rowInteractivityProps: Record<string, unknown> | undefined;
} {
  const table = useDataTable();
  const intl = useIntl();
  // used to detect if the focus event was triggered by a click
  const clicked = useRef(false);
  const { isFocusVisible, focusProps } = useFocusRing();

  if (!row.getCanActivateRow()) {
    return { rowInteractivityClassName: '', rowInteractivityProps: undefined };
  }

  const isRowDragging = table.getState().draggingRowInfo !== undefined;
  const isActivated = row.getIsActivated();
  const isAutoActivationEnabled = table.options.enableAutoActivation;
  const rowInteractivityClassName = styles.interactiveRow({
    hovered: table.getHoveredRow() === row.id,
    active: isActivated,
    focus: isFocusVisible,
    contained: table.getVisualVariantOptions().contained,
  });

  function handleRowActivation(event: ReactMouseEvent<HTMLElement>): void {
    const { target } = event;

    if (
      table.options.enableRowInteractivity !== true ||
      (target instanceof HTMLElement === false &&
        target instanceof SVGElement === false) ||
      // prevent deactivation if the user clicks on a Gantt segment (SVG <rect>) and the row is already active
      (target.nodeName === 'rect' && isActivated)
    ) {
      return;
    }

    const rowElement = target.closest('[role="row"]');
    if (rowElement && !isTargetInteractive(rowElement, target)) {
      row.toggleActivated(event);
    }
  }

  function isSelectionInsideTable(): boolean {
    const selection = globalThis.getSelection();

    if (!selection?.toString()) {
      return false;
    }

    const container = table.getRowVirtualizationContainer();
    if (!container) {
      return false;
    }

    // Use anchorNode (where the selection started) to determine whether the
    // selection originated inside the table. A selection outside the table
    // should not prevent row activation.
    let anchorNode = selection.anchorNode;
    if (anchorNode?.nodeType === Node.TEXT_NODE) {
      anchorNode = anchorNode.parentElement;
    }

    return !!anchorNode && container.contains(anchorNode);
  }

  const rowInteractivityProps = {
    tabIndex: 0,
    'data-trigger': 'interactive-row',
    'aria-label': intl.formatMessage(dataTableMessages.interactiveRowTrigger, {
      isActivated,
      rowId: row.id,
    }),
    onPointerDown: (event: PointerEvent<HTMLElement>) => {
      clicked.current = true;
    },
    onClick: (event: ReactMouseEvent<HTMLElement>) => {
      // prevent row action if the user is selecting text inside the table and ensure
      // that double click doesn't trigger the row action twice
      // (event.detail represents the click count, 1 indicates a single click)
      if (!isSelectionInsideTable() && event.detail === 1) {
        handleRowActivation(event);
      }
    },
    onAuxClick: (event: ReactMouseEvent<HTMLElement>) => {
      // only handle middle mouse button (button 1), ignore right-click and others
      if (event.button === 1) {
        handleRowActivation(event);
      }
    },
    onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => {
      if (isRowDragging) {
        return;
      }

      const { key, target } = event;

      if (
        key === 'Enter' &&
        !isAutoActivationEnabled &&
        (target instanceof HTMLElement || target instanceof SVGElement)
      ) {
        const rowElement = target.closest('[role="row"]');

        if (rowElement && !isTargetInteractive(rowElement, target)) {
          event.preventDefault();
          event.stopPropagation();
          row.toggleActivated(event);
          return;
        }
      }

      let offset: number | null = null;
      switch (key) {
        case 'ArrowDown': {
          offset = +1;
          break;
        }
        case 'ArrowUp': {
          offset = -1;
          break;
        }
        case 'PageUp': {
          offset = -10;
          break;
        }
        case 'PageDown': {
          offset = +10;
          break;
        }
        case 'Home': {
          offset = -Infinity;
          break;
        }
        case 'End': {
          offset = Infinity;
          break;
        }
      }

      if (offset === null) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      if (!(event.target instanceof HTMLElement)) {
        return;
      }

      const allRows = table.getRowModel().rows;
      const currentRowIndex = allRows.findIndex(
        (currentRow) => currentRow.id === row.id,
      );
      if (currentRowIndex === -1) {
        return;
      }

      const clampedIndex = Math.max(
        0,
        Math.min(currentRowIndex + offset, allRows.length - 1),
      );
      const nextRow: Row<unknown> | undefined = allRows.at(clampedIndex);
      if (nextRow === undefined) {
        return;
      }

      scrollToIndex(clampedIndex, {
        align: 'auto',
      });
      requestAnimationFrame(() => {
        const nextRowButton = table
          .getRowVirtualizationContainer()
          ?.querySelector<HTMLButtonElement>(
            `[data-rowid="${CSS.escape(
              nextRow.id,
            )}"][data-trigger="interactive-row"]`,
          );
        nextRowButton?.focus();
      });
    },
    ..._mergeProps(focusProps, {
      onFocus: (event: ReactFocusEvent<HTMLElement>) => {
        // only activate row if focus was triggered on the row itself
        if (event.currentTarget !== event.target) {
          return;
        }
        // When a row is initially activated, and you click on it to deactivate it using a pointing device,
        // the `pointerdown` event is called before the `focus` event. This code is needed in order to
        // not re-activate the row again. It also ensures that the focus ring is still correctly
        // moved within the table.
        if (clicked.current) {
          clicked.current = false;
          return;
        }

        if (!isActivated && isAutoActivationEnabled) {
          row.toggleActivated(event);
        }
      },
    }),
  };

  return { rowInteractivityClassName, rowInteractivityProps };
}

/**
 * Validates the event target and extracts row information.
 */
function getRowInfoFromMouseEvent(event: ReactMouseEvent<HTMLDivElement>): {
  target: HTMLElement | SVGElement;
  row: HTMLElement | null;
  rowId: string | undefined;
  isRowDetailsRow: boolean;
} | null {
  const { target } = event;

  if (!(target instanceof HTMLElement) && !(target instanceof SVGElement)) {
    return null;
  }

  const row: HTMLElement | null = target.closest('[role="row"]');
  const rowId =
    row instanceof HTMLElement && 'rowid' in row.dataset
      ? row.dataset.rowid
      : undefined;
  const isRowDetailsRow =
    rowId?.endsWith(DATA_TABLE_ROW_EXPANDED_ID_SUFFIX) ?? false;

  return { target, row, rowId, isRowDetailsRow };
}

/**
 * Defines the user action table feature
 * @internal
 */
export const DataTableRowInteractivity: TableFeature = {
  createTable<TData>(table: Table<TData>) {
    let hoveredRow: string | null = null;
    /**
     * Activates a given row for the row interactivity
     */
    table.setActiveRow = (
      updater: Updater<string | null>,
      event?: DataTableRowActivationEvent,
    ) => {
      table.options.onActiveRowChange?.(updater, event);
    };

    /**
     * Sets a given row as hovered on the table instance.
     */
    table.setHoveredRow = (rowId: string | null) => {
      if (rowId !== hoveredRow) {
        hoveredRow = rowId;
      }
    };

    /**
     * Gets a given row as hovered on the table instance.
     */
    table.getHoveredRow = () => {
      return hoveredRow;
    };

    /** Handles mouse over events for interactive rows. */
    table.handleInteractiveRowMouseOver = (
      event: ReactMouseEvent<HTMLDivElement>,
    ) => {
      if (table.options.enableRowInteractivity !== true) {
        return;
      }

      const rowInfo = getRowInfoFromMouseEvent(event);
      if (!rowInfo) {
        return;
      }

      const { target, row, rowId, isRowDetailsRow } = rowInfo;
      const virtualizationContainer = table.getRowVirtualizationContainer();

      // Remove hover styles when hovering an element outside the table or a row details row.
      // This also includes portaled content like user actions menus within the table.
      if (
        !virtualizationContainer?.contains(target) ||
        row === null ||
        isRowDetailsRow
      ) {
        // Keep hover unchanged when the target is the floating menu trigger.
        // The floating menu trigger is portaled to the document.body, but still passed as a target here.
        // This ensures hover styles don't disappear until the next mouse move when opening the floating menu.
        if (
          target instanceof HTMLElement &&
          target.dataset.trigger === 'context-action'
        ) {
          return;
        }

        if (maintainOpenMenuRowHover(table, virtualizationContainer)) {
          return;
        }

        table.setHoveredRow(null);
        removeHoverStyles(virtualizationContainer);
        return;
      }

      if (row && rowId && !isRowDetailsRow && table.getHoveredRow() !== rowId) {
        table.setHoveredRow(rowId);
        removeHoverStyles(virtualizationContainer);
        addHoverStyles(row);
      }
    };

    /** Handles mouse leave events for interactive rows. */
    table.handleInteractiveRowMouseLeave = (
      event: ReactMouseEvent<HTMLDivElement>,
    ) => {
      if (table.options.enableRowInteractivity !== true) {
        return;
      }

      const virtualizationContainer = table.getRowVirtualizationContainer();

      if (maintainOpenMenuRowHover(table, virtualizationContainer)) {
        return;
      }

      table.setHoveredRow(null);
      removeHoverStyles(virtualizationContainer);
    };

    /** Handles click events for interactive rows. */
    table.handleInteractiveRowClick = (
      event: ReactMouseEvent<HTMLDivElement>,
    ) => {
      if (table.options.enableRowInteractivity !== true) {
        return;
      }

      const rowInfo = getRowInfoFromMouseEvent(event);
      if (!rowInfo) {
        return;
      }

      const { target, row, rowId, isRowDetailsRow } = rowInfo;
      if (
        row &&
        rowId &&
        !isRowDetailsRow &&
        !isTargetInteractive(row, target)
      ) {
        event.stopPropagation();
        row.focus();
      }
    };

    assignRowMethods(table, {
      /**
       * Returns a boolean value if the row can be activated.
       */
      getCanActivateRow() {
        return table.options.enableRowInteractivity === true;
      },

      /**
       * Returns a boolean value if the row is currently activated.
       */
      getIsActivated(this: Row<TData>) {
        return table.getState().activeRow === this.id;
      },

      /**
       * Activates the row, if it is not activated.
       * Deactivates the row, if it is currently activated.
       */
      toggleActivated(this: Row<TData>, event?: DataTableRowActivationEvent) {
        table.setActiveRow(this.getIsActivated() ? null : this.id, event);
      },

      /**
       * DEPRECATION: [APPDEV-15075] BREAKING-CHANGE: Remove rowInteractivity link
       */
      getLinkForRow(this: Row<TData>) {
        return table.options.getLinkForRow?.(this);
      },
    });
  },
};

/**
 * Configuration hook for the rowInteractivity in the DataTable.
 * @internal
 */
export function useRowInteractivity<TData extends DataTableRowData>(
  props: DataTableRowInteractivityProps<TData>,
  options: TableOptions<TData>,
): void {
  const {
    activeRow: activeRowProp,
    defaultActiveRow,
    onActiveRowChange,
    interactiveRows,
  } = props;

  const [activeRow, setActiveRow] = useControllableState<string | null>({
    value: activeRowProp,
    defaultValue: defaultActiveRow,
  });
  options.enableRowInteractivity =
    interactiveRows !== undefined && interactiveRows !== false;

  // DEPRECATION: [APPDEV-15075] BREAKING-CHANGE: Remove rowInteractivity link
  const getLinkForRow = useCallback(
    (row: Row<TData>) => {
      if (
        interactiveRows !== undefined &&
        typeof interactiveRows === 'object' &&
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        interactiveRows.link
      ) {
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        return interactiveRows.link(row.original);
      }
      return undefined;
    },
    [interactiveRows],
  );

  if (options.enableRowInteractivity) {
    options.onActiveRowChange = (updater: Updater<string | null>, event) => {
      const nextState = functionalUpdate(
        updater,
        options.state?.activeRow ?? null,
      );

      setActiveRow(nextState);
      onActiveRowChange?.(nextState, event);
    };
    options.enableAutoActivation =
      interactiveRows &&
      (interactiveRows === true || interactiveRows.autoActivate !== false);

    // DEPRECATION: [APPDEV-15075] BREAKING-CHANGE: Remove rowInteractivity link
    options.getLinkForRow = getLinkForRow;
  }

  // Auto reset the state if the data changes
  const firstRun = useRef(true);
  useEffect(
    () => {
      if (firstRun.current) {
        firstRun.current = false;
        return;
      }

      if (options.enableRowInteractivity) {
        options.onActiveRowChange?.(null);
      }
    },
    // We only want to listen to the individual options parts and not the entire options.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [options.enableRowInteractivity, options.data],
  );

  options.state = {
    ...options.state,
    activeRow: activeRow ?? null,
  };
}
